import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FiHome,
  FiUsers,
  FiBarChart2,
  FiFileText,
  FiSettings,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiSearch,
  FiStar,
  FiShield,
  FiLayers,
  FiMap,
  FiMessageSquare,
  FiPhone,
  FiUpload,
  FiDownload,
  FiUserPlus,
  FiBell
} from 'react-icons/fi';
import { useAdminPendingCount } from '../../../hooks/admin';

interface AdminSidebarProps {
  open: boolean;
  collapsed: boolean;
  onClose: () => void;
  onToggleCollapse: () => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  href: string;
  badge?: number;
  children?: MenuItem[];
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({
  open,
  collapsed,
  onClose,
  onToggleCollapse
}) => {
  const location = useLocation();

  // ✅ Centralized state for tracking expanded menu items
  const [expandedItems, setExpandedItems] = React.useState<Record<string, boolean>>({});

  // ✅ Dynamic badge count from API (auto-refresh every 30 seconds)
  const { total: pendingCount } = useAdminPendingCount(true, 30000);

  const menuItems: MenuItem[] = [
    {
      id: 'dashboard',
      label: 'แผงควบคุม',
      icon: FiHome,
      href: '/admin/dashboard'
    },
    {
      id: 'companies',
      label: 'จัดการบริษัท',
      icon: FiHome,
      href: '/admin/companies',
      children: [
        { id: 'companies-list', label: 'รายการบริษัท', icon: FiHome, href: '/admin/companies' },
        { id: 'companies-add', label: 'เพิ่มบริษัทใหม่', icon: FiHome, href: '/admin/companies/add' },
        { id: 'companies-import', label: 'Import ข้อมูล', icon: FiUpload, href: '/admin/companies/import' },
        { id: 'companies-featured', label: 'บริษัทแนะนำ', icon: FiStar, href: '/admin/featured-companies' },
        { id: 'service-types', label: 'ประเภทบริการ', icon: FiLayers, href: '/admin/service-types' },
        { id: 'provinces', label: 'จังหวัด', icon: FiMap, href: '/admin/provinces' }
      ]
    },
    {
      id: 'users',
      label: 'จัดการผู้ใช้',
      icon: FiUsers,
      href: '/admin/users',
      children: [
        { id: 'users-list', label: 'รายการผู้ใช้', icon: FiUsers, href: '/admin/users' },
        { id: 'users-add', label: 'เพิ่มผู้ใช้', icon: FiUserPlus, href: '/admin/users/add' },
        { id: 'users-roles', label: 'จัดการสิทธิ์', icon: FiShield, href: '/admin/users/roles' }
      ]
    },
    {
      id: 'analytics',
      label: 'สถิติและรายงาน',
      icon: FiBarChart2,
      href: '/admin/analytics',
      children: [
        { id: 'analytics-overview', label: 'ภาพรวม', icon: FiBarChart2, href: '/admin/analytics' },
        { id: 'analytics-companies', label: 'สถิติบริษัท', icon: FiHome, href: '/admin/analytics/companies' },
        { id: 'analytics-users', label: 'สถิติผู้ใช้', icon: FiUsers, href: '/admin/analytics/users' },
        { id: 'analytics-search', label: 'สถิติการค้นหา', icon: FiSearch, href: '/admin/analytics/search' },
        { id: 'analytics-contacts', label: 'สถิติการติดต่อ', icon: FiPhone, href: '/admin/analytics/contacts' }
      ]
    },
    {
      id: 'requests',
      label: 'คำขออนุมัติ',
      icon: FiFileText,
      href: '/admin/all-requests',
      badge: pendingCount // ✅ Dynamic badge from API
    },
    {
      id: 'feedback',
      label: 'Feedback',
      icon: FiMessageSquare,
      href: '/admin/feedback'
    },
    {
      id: 'notifications',
      label: 'การแจ้งเตือน',
      icon: FiBell,
      href: '/admin/notifications'
    },
    {
      id: 'settings',
      label: 'ตั้งค่า',
      icon: FiSettings,
      href: '/admin/settings'
    }
  ];

  const isActiveRoute = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  // ✅ Auto-expand menus that contain active routes
  React.useEffect(() => {
    const newExpandedItems: Record<string, boolean> = {};
    menuItems.forEach(item => {
      if (item.children) {
        const hasActiveChild = item.children.some(child => isActiveRoute(child.href));
        if (hasActiveChild) {
          newExpandedItems[item.id] = true;
        }
      }
    });
    setExpandedItems(newExpandedItems);
  }, [location.pathname]);

  const renderMenuItem = (item: MenuItem, level = 0) => {
    const isActive = isActiveRoute(item.href);
    const hasChildren = item.children && item.children.length > 0;
    const expanded = expandedItems[item.id] || false;

    const toggleExpanded = () => {
      setExpandedItems(prev => ({
        ...prev,
        [item.id]: !prev[item.id]
      }));
    };

    return (
      <div key={item.id} className="mb-1">
        <div className="flex items-center">
          <Link
            to={item.href}
            className={`flex items-center w-full px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
              isActive
                ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-500'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            } ${level > 0 ? 'ml-6' : ''}`}
            onClick={() => {
              if (window.innerWidth < 1024) onClose();
            }}
          >
            <item.icon className={`${collapsed ? 'mx-auto' : 'mr-3'} h-5 w-5 flex-shrink-0`} />
            {!collapsed && (
              <>
                <span className="flex-1">{item.label}</span>
                {item.badge && (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                    {item.badge}
                  </span>
                )}
              </>
            )}
          </Link>
          {hasChildren && !collapsed && (
            <button
              onClick={toggleExpanded}
              className="p-1 ml-1 text-gray-400 hover:text-gray-600"
            >
              {expanded ? <FiChevronLeft /> : <FiChevronRight />}
            </button>
          )}
        </div>
        {hasChildren && expanded && !collapsed && (
          <div className="mt-1 ml-4">
            {item.children?.map((child) => renderMenuItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Desktop sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 bg-white border-r border-gray-200 transition-all duration-300 ease-in-out ${
          collapsed ? 'w-16' : 'w-64'
        } hidden lg:flex lg:flex-col`}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          {!collapsed && (
            <Link to="/admin/dashboard" className="flex items-center">
              <div className="flex-shrink-0">
                <FiShield className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-3">
                <h1 className="text-lg font-semibold text-gray-900">Admin PanelX</h1>
                <p className="text-xs text-gray-500">Thai Security Directory</p>
              </div>
            </Link>
          )}
          <button
            onClick={onToggleCollapse}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            {collapsed ? <FiChevronRight /> : <FiChevronLeft />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          {menuItems.map((item) => renderMenuItem(item))}
        </nav>
      </div>

      {/* Mobile sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:hidden ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Mobile header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          <Link to="/admin/dashboard" className="flex items-center">
            <FiShield className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <h1 className="text-lg font-semibold text-gray-900">Admin Panel</h1>
              <p className="text-xs text-gray-500">Thai Security Directory</p>
            </div>
          </Link>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <FiX className="h-6 w-6" />
          </button>
        </div>

        {/* Mobile navigation */}
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          {menuItems.map((item) => renderMenuItem(item))}
        </nav>
      </div>
    </>
  );
};

export default AdminSidebar;